import { useLayoutEffect, useRef, useState, type RefObject } from 'react'
import { PARTNERS_MARQUEE } from '../constants/home'
import type { PartnerLogo } from '../helpers/mapContent'
import {
  buildPartnerMarqueeSequence,
  partnerMarqueeCopies,
  partnerMarqueeDurationSec,
} from '../helpers/partners'

type PartnerMarqueeState = {
  sequence: PartnerLogo[]
  durationSec: number
  viewportRef: RefObject<HTMLDivElement | null>
  measureRef: RefObject<HTMLDivElement | null>
}

export function usePartnerMarquee(logos: PartnerLogo[]): PartnerMarqueeState {
  const viewportRef = useRef<HTMLDivElement | null>(null)
  const measureRef = useRef<HTMLDivElement | null>(null)
  const [copies, setCopies] = useState(2)

  useLayoutEffect(() => {
    if (logos.length === 0) {
      setCopies(0)
      return
    }

    function measure() {
      const viewportWidth = viewportRef.current?.clientWidth ?? window.innerWidth
      const measureEl = measureRef.current
      const itemWidth =
        measureEl && measureEl.childElementCount > 0 && measureEl.scrollWidth > 0
          ? measureEl.scrollWidth / measureEl.childElementCount
          : PARTNERS_MARQUEE.fallbackItemWidthPx

      setCopies(
        partnerMarqueeCopies(
          logos.length,
          itemWidth,
          viewportWidth,
          PARTNERS_MARQUEE.minItems,
        ),
      )
    }

    measure()

    const images = measureRef.current?.querySelectorAll('img') ?? []
    images.forEach((image) => image.addEventListener('load', measure))
    window.addEventListener('resize', measure)

    return () => {
      images.forEach((image) => image.removeEventListener('load', measure))
      window.removeEventListener('resize', measure)
    }
  }, [logos])

  const sequence = buildPartnerMarqueeSequence(logos, copies)
  const durationSec = partnerMarqueeDurationSec(
    sequence.length,
    PARTNERS_MARQUEE.durationSec,
    PARTNERS_MARQUEE.minItems,
  )

  return { sequence, durationSec, viewportRef, measureRef }
}
